import type { Edge } from '@xyflow/react';

import { applyAutoLayout } from '@/components/fluxograma/autoLayout';
import { mapFlowEdgesToApi, mapFlowNodesToApi } from '@/components/organograma/organogramaMappers';
import type { OrgMvpCardData, OrgMvpFigure, OrgMvpNode } from '@/components/organograma/organogramaTypes';
import type { ResultadoGeracao, TipoNoGerado } from './organogramaGerador';

// Mesmas cores/figuras do importador de planilha (organogramaImportBuilder.ts).
const DEPARTAMENTO_COLOR = '#059669';
const PESSOA_COLOR = '#2563EB';
const SEM_DEPARTAMENTO_COLOR = '#64748B';
// Card de quadro (cargo sem ninguém cadastrado): cor própria para não ser confundido com uma pessoa.
const QUADRO_COLOR = '#D97706';

const LARGURA = 220;
const ALTURA = 118;

const ESTILO_POR_TIPO: Record<TipoNoGerado, { color: string; figure: OrgMvpFigure }> = {
  empresa: { color: DEPARTAMENTO_COLOR, figure: { type: 'catalog', key: 'building' } },
  departamento: { color: DEPARTAMENTO_COLOR, figure: { type: 'catalog', key: 'team' } },
  sem_departamento: { color: SEM_DEPARTAMENTO_COLOR, figure: { type: 'catalog', key: 'team' } },
  pessoa: { color: PESSOA_COLOR, figure: { type: 'catalog', key: 'person' } },
  cargo_quadro: { color: QUADRO_COLOR, figure: { type: 'catalog', key: 'team' } },
};

let contador = 0;
const novoId = (prefixo: string) => {
  contador += 1;
  return `gerado_${prefixo}_${Date.now().toString(36)}_${contador}`;
};

/** Converte a árvore pura do gerador em nós/arestas do React Flow, já com auto-layout (TB). */
export function montarNosEArestas(resultado: ResultadoGeracao): { nodes: OrgMvpNode[]; edges: Edge[] } {
  const idPorChave = new Map<string, string>();

  const nodes: OrgMvpNode[] = resultado.nos.map((no) => {
    const id = novoId(no.tipo);
    idPorChave.set(no.chave, id);
    const estilo = ESTILO_POR_TIPO[no.tipo];
    const data: OrgMvpCardData = {
      kind: no.tipo === 'pessoa' || no.tipo === 'cargo_quadro' ? 'person' : 'department',
      label: no.rotulo,
      subtitle: no.subtitulo,
      description: no.descricao,
      color: estilo.color,
      textColor: '#0F172A',
      figure: estilo.figure,
    };
    if (no.tipo === 'pessoa') {
      data.sourceType = 'SYSTEM_USER';
      data.usuarioId = no.usuarioId;
      data.departamentoId = no.departamentoId;
    } else if (no.tipo === 'departamento') {
      data.departamentoId = no.departamentoId;
    } else if (no.tipo === 'cargo_quadro') {
      // Posição do quadro, sem usuário por trás: card comum do organograma, editável depois.
      data.sourceType = 'ORG_ONLY';
      data.departamentoId = no.departamentoId;
    }
    return {
      id,
      type: 'card',
      position: { x: 0, y: 0 },
      width: LARGURA,
      height: ALTURA,
      zIndex: 10,
      style: { width: LARGURA, height: ALTURA },
      data,
    };
  });

  const corPorId = new Map(nodes.map((node) => [node.id, (node.data as OrgMvpCardData).color]));
  const edges: Edge[] = resultado.arestas.map((aresta) => {
    const source = idPorChave.get(aresta.origem) as string;
    const target = idPorChave.get(aresta.destino) as string;
    return {
      id: novoId('edge'),
      source,
      target,
      type: 'smoothstep',
      style: { stroke: corPorId.get(target) ?? PESSOA_COLOR, strokeWidth: 2.2 },
      data: { tipo: 'direta' },
    };
  });

  const { nodes: posicionados } = applyAutoLayout(nodes, edges, { direction: 'TB', nodeWidth: LARGURA, nodeHeight: ALTURA });
  return { nodes: posicionados as OrgMvpNode[], edges };
}

/**
 * Payload aceito por `PUT /api/organogramas/:id/canvas` — usa a mesma conversão do editor
 * (`mapFlowNodesToApi`/`mapFlowEdgesToApi`). Sem `updatedAt`: é o primeiro save do organograma recém-criado.
 */
export function montarPayloadCanvas(resultado: ResultadoGeracao) {
  const { nodes, edges } = montarNosEArestas(resultado);
  return { nodes: mapFlowNodesToApi(nodes), edges: mapFlowEdgesToApi(edges) };
}
